import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TableRow from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableRow';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import TableCell from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableCell';
import { getBorderRadius } from 'Components/utils';
import {
  useDivideColumns,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionAssumptionsAndDetails/Layout/ScenarioSelectionDetailedSummaryPicker/ScenarioSelectionDetailedSummary/ScenarioSelectionDetailedSummarySections/useDividedColumns';
import {
  ScenarioSelectionDetailedSummaryColoredValue,
} from 'Views/Page/Content/Modal/Layouts/ScenarioSelectionAssumptionsAndDetails/Layout/ScenarioSelectionDetailedSummaryPicker/ScenarioSelectionDetailedSummary/ScenarioSelectionDetailedSummarySections/ScenarioSelectionDetailedSummaryColoredValue';
import { ScenarioSelectionDetailedSummaryStickyCell } from './ScenarioSelectionDetailedSummaryStickyCell';

const useStyles = buildUseStyles('ScenarioSelectionDetailedSummarySectionRow', (theme) => ({
  emphasized: {
    '& > td': {
      backgroundColor: theme.palette.facet.oceana800,
      '&:first-child': getBorderRadius(theme.radius.md, { left: true }),
      '&:last-child': getBorderRadius(theme.radius.md, { right: true }),
      '&:only-child': getBorderRadius(theme.radius.md, { all: true }),
    },
    '& .FD-MuiTypography-root': {
      color: theme.palette.common.white,
    },
  },
  noBorder: {
    border: 'none',
  },
}));

const ScenarioSelectionDetailedSummarySectionRow = ({
  LabelComponent,
  labelSize,
  ValueComponent,
  valueSize,
  valueColoredBySign,
  emphasized,
  level,
  row,
  handleToggleOpen,
  test,
  expanded,
  isExpandable,
  showMonths: monthsAreNotHidden,
  dense,
}) => {
  const showMonths = monthsAreNotHidden && row.data?.length > 0;
  const classes = useStyles();
  const allColumnContent = useMemo(() => ([
    <ScenarioSelectionDetailedSummaryColoredValue
      ValueComponent={ValueComponent}
      valueColoredBySign={valueColoredBySign}
      valueSize={valueSize}
      rawValue={row.subLabel}
      hint={row.hint}
    />,
    ...row.data.map(cell => (
      <ScenarioSelectionDetailedSummaryColoredValue
        key={cell.id}
        ValueComponent={ValueComponent}
        valueSize={valueSize}
        valueColoredBySign={valueColoredBySign}
        rawValue={cell.value}
        tooltip={cell.altValue}
        hint={cell.hint}
      />
    )),
  ]), [row, valueSize, ValueComponent]);
  const { stickyColumn, nonStickyColumns } = useDivideColumns({ showMonths, allColumnContent });
  if (!LabelComponent || !ValueComponent) {
    return null;
  }
  const content = (
    <TableRow
      className={classNames({
        [classes.emphasized]: emphasized,
        [classes.notTopLevelBorder]: level !== 0,
      })}
      onClick={() => handleToggleOpen(row.id)}
    >
      <ScenarioSelectionDetailedSummaryStickyCell
        isExpandable={isExpandable}
        expanded={expanded}
        showMonths={showMonths}
        level={level}
        dense={dense}
        background={emphasized ? 'emphasized' : undefined}
        label={<LabelComponent flex={1} Component value={row.label} size={labelSize} />}
        stickyColumn={stickyColumn}
        test={test}
      />
      {nonStickyColumns}
    </TableRow>
  );
  if (!emphasized) {
    return content;
  }
  // In the design, emphasized rows are given extra spacing for some reason, and we can't just add margin/padding.
  // There is a trick we can use involving adding a border, but that messes up the border-radius.
  // Instead, just use some empty rows.
  return (
    <>
      <TableRow data-empty-row>
        <TableCell className={classes.noBorder} />
      </TableRow>
      {content}
      <TableRow data-empty-row>
        <TableCell className={classes.noBorder} />
      </TableRow>
    </>
  );
};

ScenarioSelectionDetailedSummarySectionRow.defaultProps = {
  labelSize: 'md',
  valueSize: 'md',
  isExpandable: false,
};

ScenarioSelectionDetailedSummarySectionRow.propTypes = {
  LabelComponent: PropTypes.object.isRequired,
  labelSize: PropTypes.string,
  ValueComponent: PropTypes.object.isRequired,
  valueSize: PropTypes.string,
  valueColoredBySign: PropTypes.bool.isRequired,
  emphasized: PropTypes.bool.isRequired,
  row: PropTypes.object.isRequired,
  handleToggleOpen: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  isExpandable: PropTypes.bool,
  showMonths: PropTypes.bool.isRequired,
  level: PropTypes.number.isRequired,
  test: PropTypes.string.isRequired,
  dense: PropTypes.bool.isRequired,
};

export default ScenarioSelectionDetailedSummarySectionRow;
